import React, { useRef, useState } from 'react';
import { useDelayedAutoFocus } from '@/hooks/useDelayedAutoFocus';
import { Keyboard, View } from 'react-native';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { VStack } from '@/components/gluestack/vstack';
import { Input, InputField, InputSlot, InputIcon } from '@/components/gluestack/input';
import { FormControl, FormControlError, FormControlErrorIcon, FormControlErrorText, FormControlLabel, FormControlLabelText } from '@/components/gluestack/form-control';
import { Button, ButtonText } from '@/components/gluestack/button';
import { AlertCircleIcon, EyeIcon, EyeOffIcon } from '@/components/gluestack/icon';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useToastMessages } from '@/hooks/useToastMessages';
import { AccountService } from '@/services/domain/AccountService';
type FormValues = { oldPassword: string; newPassword: string; confirmPassword: string };

export interface IPasswordChangeSheetProps extends IContextSheetContentProps {}

export default function PasswordChangeSheet({ onClose }: IPasswordChangeSheetProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const [submitting, setSubmitting] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const oldPasswordRef = useDelayedAutoFocus();
  const newPasswordRef = useRef<any>(null);
  const confirmPasswordRef = useRef<any>(null);

  const schema = z
    .object({
      oldPassword: z.string().min(1, { message: t('settings.account.password.oldRequired') }),
      newPassword: z.string().min(6, { message: t('common.validation.min', { minLength: 6 }) }),
      confirmPassword: z.string().min(6, { message: t('common.validation.min', { minLength: 6 }) }),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: 'mismatch',
      path: ['confirmPassword'],
    });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { oldPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = async ({ oldPassword, newPassword }: FormValues) => {
    Keyboard.dismiss();
    setSubmitting(true);
    try {
      await AccountService.changePassword({ OldPassword: oldPassword, NewPassword1: newPassword, NewPassword2: newPassword });
      showSuccessToast(t('settings.account.password.success'));
      onClose?.();
    } catch (error) {
      showErrorToast(t('settings.account.password.error'), error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <VStack className="flex-1 gap-4">
      <FormControl isInvalid={!!errors.oldPassword}>
        <FormControlLabel>
          <FormControlLabelText>{t('settings.account.password.old')}</FormControlLabelText>
        </FormControlLabel>
        <Controller
          control={control}
          name="oldPassword"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                ref={oldPasswordRef}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="next"
                submitBehavior="submit"
                onSubmitEditing={() => setTimeout(() => newPasswordRef.current?.focus(), 0)}
                type={showOld ? 'text' : 'password'}
              />
              <InputSlot onPress={() => setShowOld((s) => !s)} className="pr-3">
                <InputIcon as={showOld ? EyeIcon : EyeOffIcon} />
              </InputSlot>
            </Input>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText>{errors.oldPassword?.message}</FormControlErrorText>
        </FormControlError>
      </FormControl>

      <FormControl isInvalid={!!errors.newPassword}>
        <FormControlLabel>
          <FormControlLabelText>{t('settings.account.password.new')}</FormControlLabelText>
        </FormControlLabel>
        <Controller
          control={control}
          name="newPassword"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                ref={newPasswordRef}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="next"
                submitBehavior="submit"
                onSubmitEditing={() => setTimeout(() => confirmPasswordRef.current?.focus(), 0)}
                type={showNew ? 'text' : 'password'}
              />
              <InputSlot onPress={() => setShowNew((s) => !s)} className="pr-3">
                <InputIcon as={showNew ? EyeIcon : EyeOffIcon} />
              </InputSlot>
            </Input>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText>{errors.newPassword?.message}</FormControlErrorText>
        </FormControlError>
      </FormControl>

      <FormControl isInvalid={!!errors.confirmPassword}>
        <FormControlLabel>
          <FormControlLabelText>{t('settings.account.password.repeat')}</FormControlLabelText>
        </FormControlLabel>
        <Controller
          control={control}
          name="confirmPassword"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                ref={confirmPasswordRef}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="done"
                onSubmitEditing={handleSubmit(onSubmit)}
                type={showConfirm ? 'text' : 'password'}
              />
              <InputSlot onPress={() => setShowConfirm((s) => !s)} className="pr-3">
                <InputIcon as={showConfirm ? EyeIcon : EyeOffIcon} />
              </InputSlot>
            </Input>
          )}
        />
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText>
            {errors.confirmPassword?.message === 'mismatch' ? t('settings.account.password.mismatch') : errors.confirmPassword?.message}
          </FormControlErrorText>
        </FormControlError>
      </FormControl>

      <View className="flex-1" />

      <Button onPress={handleSubmit(onSubmit)} isDisabled={submitting} className="mt-2 self-end">
        <ButtonText>{t('common.update')}</ButtonText>
      </Button>
    </VStack>
  );
}

PasswordChangeSheet.sheetConfig = {
  largeScreenMode: 'drawer',
  size: 'sm',
  height: 'lg',
  expectsKeyboard: true,
} satisfies ISheetConfig;
